import { useCallback, useEffect, useState } from 'react';
import type { Screen } from '@hub/shared';

/**
 * Drobny router nad hashem. Staci na levé menu a dovoluje odkazy
 * typu `#/zakaznici/<id>`, aby se dalo poslat odkaz na kartu.
 */
export interface Route {
  screen: Screen;
  param?: string;
}

const SCREEN_PATH: Record<Screen, string> = {
  overview: 'prehled',
  races: 'zavody',
  customers: 'zakaznici',
  trips: 'cesty',
  communication: 'komunikace',
  compose: 'odeslat-rozesilku',
  transactional: 'transakcni-zpravy',
  reviews: 'hodnoceni',
  exceptions: 'vyjimky',
  revenue: 'vynosy',
  data_quality: 'kvalita-dat',
  jobs: 'ulohy',
  settings: 'nastaveni',
  simulator: 'simulator',
  care: 'zakaznicka-pece',
  web_behavior: 'chovani-na-webu',
  deliverability: 'dorucitelnost',
};

const PATH_SCREEN = Object.fromEntries(
  Object.entries(SCREEN_PATH).map(([screen, path]) => [path, screen as Screen]),
) as Record<string, Screen>;

export function hrefFor(screen: Screen, param?: string): string {
  return `#/${SCREEN_PATH[screen]}${param ? `/${encodeURIComponent(param)}` : ''}`;
}

function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const screen = parts[0] ? PATH_SCREEN[parts[0]] : undefined;
  return {
    screen: screen ?? 'overview',
    ...(parts[1] ? { param: decodeURIComponent(parts[1]) } : {}),
  };
}

export function useRoute(): [Route, (screen: Screen, param?: string) => void] {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash));

  useEffect(() => {
    const onChange = (): void => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const go = useCallback((screen: Screen, param?: string) => {
    window.location.hash = hrefFor(screen, param).slice(1);
  }, []);

  return [route, go];
}
